/*----------------------------------
- GITHUB INTENT HELPERS
----------------------------------*/

/**
 * @fix  docs/fixes/2026-07-17-website-auth-conversion-resilience-regressions.md
 */

import type * as Desired from '@/server/services/Conversion/Desired';
import { appErrorReporter } from '@/common/libs/errorReporting';
import { RADAR_CREATE_ACTION } from '@uniquedomains/components/contracts/upgrade/catalogs/featureActions';
import { buildWebsiteAuthHref } from '@/client/utils/conversion/authHref';
import { withConversionDeadline } from '@uniquedomains/components/conversion/modal/utils/deadline';
import type { WebsiteConversionContext } from '@uniquedomains/components/conversion/authIntent';
import {
    PRICING_PLAN_CATALOG,
    SELLABLE_PRICING_PLAN_ORDER,
    type SellablePricingPlanId,
} from '@uniquedomains/components/pricing/catalogs/pricingPlans';

/*----------------------------------
- TYPES
----------------------------------*/

export type GithubIntent = 'radar' | 'project';
export type GithubIntentPersona = 'investor' | 'founder';

export type GithubIntentActionCopy = {
    intent: GithubIntent;
    label: string;
    persona: GithubIntentPersona;
    /**
     * The plan the intent pre-arms.
     *
     * Typed as the sellable id rather than a hand-written union: both actions used
     * to name a retired plan (`pro` for the investor path, `launch` for the
     * founder path), and `readWebsitePlan` now discards anything unsellable, so
     * the handoff silently degraded to `plan=free&planSelection=default` and the
     * pre-arm stopped happening at all.
     */
    planId: SellablePricingPlanId;
    planLabel: string;
};

export type GithubIntentExperience = {
    focusIntent: GithubIntent | null;
    primaryAction: GithubIntentActionCopy;
    secondaryAction: GithubIntentActionCopy;
    banner: {
        eyebrow: string;
        title: string;
        description: string;
    } | null;
};

export type GithubIntentOutcome = {
    intent: GithubIntent;
    href: string;
    cleanedUrl: string;
    desired: Desired.TDesired;
} | null;

type ResolveGithubIntentOutcomeInput = {
    inputUrl: string;
    productSearchUrl: string;
    /**
     * What the visitor was looking at when the intent fired. Taken from the page rather
     * than rebuilt here: on a GitHub dataset page it is the dataset context naming the
     * repository, and reading it from the caller is what keeps this auto-opened modal
     * describing the same page as every manual CTA on it.
     */
    context?: WebsiteConversionContext | null;
};

type ProductSessionReader = () => PromiseLike<{ email: string } | null>;

/*----------------------------------
- CONST
----------------------------------*/

const GITHUB_INTENT_QUERY_KEY = 'github_intent';
export const GITHUB_INTENT_SESSION_TIMEOUT_MS = 2_000;
/* One paid plan, so both personas pre-arm the same one. Persona still shapes the
   banner and the label; it no longer picks a tier. */
const PRE_ARMED_PLAN_ID: SellablePricingPlanId = SELLABLE_PRICING_PLAN_ORDER[0];
const PRE_ARMED_PLAN_LABEL = PRICING_PLAN_CATALOG[PRE_ARMED_PLAN_ID].marketingTagShort;

const RADAR_ACTION_COPY: GithubIntentActionCopy = {
    intent: 'radar',
    label: 'Create Radar',
    persona: 'investor',
    planId: PRE_ARMED_PLAN_ID,
    planLabel: PRE_ARMED_PLAN_LABEL,
};
// The `project` intent id, its `github_dataset_project` source and the
// `create-project` desired action stay as persisted identifiers: the GitHub dataset
// READMEs link `?github_intent=project`, and Product reads the ids. Only the label
// moved when Projects merged into Radars (ADR-0051): the founder path now starts a
// Radar from a search.
const PROJECT_ACTION_COPY: GithubIntentActionCopy = {
    intent: 'project',
    label: 'Start a Radar',
    persona: 'founder',
    planId: PRE_ARMED_PLAN_ID,
    planLabel: PRE_ARMED_PLAN_LABEL,
};

/*----------------------------------
- HELPERS
----------------------------------*/

const toParsedUrl = (inputUrl: string): URL | null => {
    try {
        return new URL(inputUrl, 'https://unique.domains');
    } catch (urlError) {
        appErrorReporter.app.handleError(urlError);
        console.warn('[github-intent] Failed to parse GitHub intent URL.', urlError);
        return null;
    }
};

const isGithubIntent = (value: string | null): value is GithubIntent => {
    return value === 'radar' || value === 'project';
};

const buildDesired = (intent: GithubIntent, productSearchUrl: string): Desired.TDesired => {
    return {
        page: productSearchUrl,
        feature: 'radarsCount',
        action: intent === 'project' ? 'create-project' : RADAR_CREATE_ACTION,
        from: `github_dataset_${intent}`,
    };
};

/*----------------------------------
- PUBLIC API
----------------------------------*/

export const readGithubIntent = (inputUrl: string): GithubIntent | null => {
    const parsed = toParsedUrl(inputUrl);
    if (!parsed) return null;

    const rawIntent = parsed.searchParams.get(GITHUB_INTENT_QUERY_KEY)?.trim().toLowerCase() || null;
    return isGithubIntent(rawIntent) ? rawIntent : null;
};

export const resolveGithubIntentProductAuthentication = async (
    knownAuthenticated: boolean,
    readSession: ProductSessionReader,
): Promise<boolean> => {
    if (knownAuthenticated) return true;

    const session = await withConversionDeadline(
        readSession(),
        GITHUB_INTENT_SESSION_TIMEOUT_MS,
        'Product session check timed out.',
    );
    return Boolean(session?.email);
};

export const getGithubIntentExperience = (intent: GithubIntent | null): GithubIntentExperience => {
    if (intent === 'project') {
        return {
            focusIntent: intent,
            primaryAction: PROJECT_ACTION_COPY,
            secondaryAction: RADAR_ACTION_COPY,
            banner: {
                eyebrow: 'GitHub founder path armed',
                /* "brief" was a noun here (website-copy.md); the mock's "a search for this exact search" read
                   "search" twice, so Gaetan chose this line on 2026-10-27 (batch 7 report, decision 76). */
                title: 'Start a Radar from this exact search',
                description: `This page keeps the search context and pre-arms the founder flow on ${PRE_ARMED_PLAN_LABEL}.`,
            },
        };
    }

    if (intent === 'radar') {
        return {
            focusIntent: intent,
            primaryAction: RADAR_ACTION_COPY,
            secondaryAction: PROJECT_ACTION_COPY,
            banner: {
                eyebrow: 'GitHub investor path armed',
                title: 'Create Radar from this exact search',
                description: `This page keeps the search context and pre-arms the investor flow on ${PRE_ARMED_PLAN_LABEL}.`,
            },
        };
    }

    return {
        focusIntent: null,
        primaryAction: RADAR_ACTION_COPY,
        secondaryAction: PROJECT_ACTION_COPY,
        banner: null,
    };
};

export const stripGithubIntentFromUrl = (inputUrl: string): string => {
    const parsed = toParsedUrl(inputUrl);
    if (!parsed) return inputUrl;

    parsed.searchParams.delete(GITHUB_INTENT_QUERY_KEY);
    const query = parsed.searchParams.toString();

    return `${parsed.pathname}${query ? `?${query}` : ''}${parsed.hash}`;
};

export const resolveGithubIntentOutcome = ({
    inputUrl,
    productSearchUrl,
    context,
}: ResolveGithubIntentOutcomeInput): GithubIntentOutcome => {
    const intent = readGithubIntent(inputUrl);
    if (!intent) return null;

    const desired = buildDesired(intent, productSearchUrl);
    const cleanedUrl = stripGithubIntentFromUrl(inputUrl);
    const action = intent === 'project' ? PROJECT_ACTION_COPY : RADAR_ACTION_COPY;

    return {
        intent,
        href: buildWebsiteAuthHref({
            ...desired,
            flow: 'cold',
            mode: 'signup',
            /* No persona. A dataset link says which dataset the visitor came from, never
               who they are: the persona here was the catalog's guess for that action, and
               a guess is exactly what a link may no longer carry. The dataset context
               below still travels, so the modal keeps its dataset headline without the
               assumption. */
            plan: action.planId,
            /* The same product search `desired.page` names, sent under the key the builder
               actually serializes. `desired` keeps `page` because analytics reads it. */
            returnTo: productSearchUrl,
            ...(context ? { context } : {}),
            /* Not a wall. The visitor followed a dataset link to create something, so the
               modal keeps its dataset headline and its persona question (ADR-0050); `create`
               would otherwise ride the link as the Create radar wall. `desired.action` still
               records it for analytics. */
            action: undefined,
        }),
        cleanedUrl,
        desired,
    };
};
